import { ReactNode } from 'react';
import Button from './Button';

interface IntegrationCardProps {
  title: string;
  description: string;
  /** Unknown while the card is still loading its status. */
  enabled: boolean | null;
  busy?: boolean;
  onToggle: (next: boolean) => void;
  /** Omitted when the integration has nothing to configure yet. */
  onConfigure?: () => void;
  /** Connect or configure body. Omitted while the card is idle. */
  panel?: ReactNode;
}

/**
 * One integration on the Admin catalog. The switch turns it on immediately:
 * the knob moves right and the track turns green. Turning it off reverses that.
 * Configure appears only while it is on. Further cards reuse this shell.
 */
const IntegrationCard = ({
  title,
  description,
  enabled,
  busy = false,
  onToggle,
  onConfigure,
  panel,
}: IntegrationCardProps) => {
  const on = enabled === true;

  return (
    <article className="bg-surface border border-line rounded-card shadow-azure-sm p-5">
      <h2 className="text-title font-semibold text-ink">{title}</h2>
      <p className="mt-1 text-body text-ink-muted">{description}</p>
      <div className="mt-5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          {enabled !== null && (
            <span
              className={`inline-flex items-center h-8 px-3 rounded-control text-body font-medium ${
                on ? 'bg-success-subtle text-success' : 'bg-surface-sunken text-ink'
              }`}
            >
              {on ? 'Active' : 'Inactive'}
            </span>
          )}
          {on && onConfigure && (
            <Button variant="secondary" size="sm" onClick={onConfigure}>
              Configure
            </Button>
          )}
        </div>
        <label className={`relative inline-flex h-6 w-11 shrink-0 items-center ${busy ? 'cursor-wait' : 'cursor-pointer'}`}>
          <input
            type="checkbox"
            role="switch"
            aria-label={title}
            checked={on}
            disabled={enabled === null || busy}
            onChange={(event) => onToggle(event.target.checked)}
            className="peer sr-only"
          />
          <span
            className={`h-6 w-11 rounded-full transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-success peer-focus-visible:ring-offset-2 peer-disabled:opacity-50 ${
              on ? 'bg-success' : 'bg-line-strong'
            }`}
          />
          <span
            className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-azure-sm transition-transform peer-disabled:opacity-50 ${
              on ? 'translate-x-5' : 'translate-x-0'
            }`}
          />
        </label>
      </div>
      {panel && <div className="mt-4 pt-4 border-t border-line">{panel}</div>}
    </article>
  );
};

export default IntegrationCard;
